import { useCallback, useEffect, useRef, useState } from "react";

/** Одна страница ленты: сами объекты плюс размер всей ленты. */
export interface ChatMediaPage<T> {
    items: T[];
    total: number;
}

interface UseChatMediaPagesParams<T> {
    /** Смена значения = другая лента (другой чат / другой фильтр) → начать заново. */
    resetKey: string;
    enabled: boolean;
    /** Загрузить страницу: без курсора — начало ленты, с курсором — то, что старше. */
    loadPage: (cursor: string | undefined) => Promise<ChatMediaPage<T>>;
}

export interface ChatMediaPages<T> {
    items: T[];
    /** Размер всей ленты — против него видно, сколько ещё не загружено. */
    total: number;
    hasMore: boolean;
    /** Первая страница ещё едет — грид пока пустой. */
    isLoading: boolean;
    /** Догрузка к уже показанному — грид остаётся на месте. */
    isLoadingMore: boolean;
    isError: boolean;
    loadMore: () => void;
    refetch: () => void;
}

const EMPTY_STATE = { items: [], total: 0, exhausted: false };

/**
 * Лента медиа чата, дочитываемая страницами «вниз».
 *
 * Сервер отдаёт страницу и размер всей ленты, а курсором служит id последнего
 * показанного объекта — поэтому число медиа в чате ничем не ограничено,
 * ограничен только размер одной страницы. Прежняя схема (один запрос с
 * потолком) делала всё, что старше потолка, недостижимым: в импортированном
 * чате пикер терял часть картинок совсем.
 *
 * Двусторонняя догрузка тут не нужна — пикер всегда открывается с начала ленты
 * и идёт вниз (окно вокруг произвольного объекта — это лайтбокс, см.
 * `useChatArtifactFeed`).
 */
export function useChatMediaPages<T extends { id: string }>({
    resetKey,
    enabled,
    loadPage,
}: UseChatMediaPagesParams<T>): ChatMediaPages<T> {
    const [state, setState] = useState<{ items: T[]; total: number; exhausted: boolean }>(EMPTY_STATE);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isError, setIsError] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    // Курсоры, по которым догрузка уже сходила: прокрутка у нижнего края иначе
    // дёргала бы одну и ту же страницу на каждое движение колеса.
    const requested = useRef<Set<string>>(new Set());
    const loading = useRef(false);
    // Загрузчик пересоздаётся на каждый рендер (замыкает параметры фичи), но
    // перезапуск ленты обязан зависеть только от `resetKey` — иначе первая
    // страница грузилась бы бесконечно.
    const loadPageRef = useRef(loadPage);
    loadPageRef.current = loadPage;

    useEffect(() => {
        requested.current = new Set();
        if (!enabled) {
            setState(EMPTY_STATE);
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        setIsError(false);
        void loadPageRef
            .current(undefined)
            .then((page) => {
                if (cancelled) return;
                setState({ items: page.items, total: page.total, exhausted: page.items.length === 0 });
                setIsLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("chat media feed: failed to load", err);
                setIsError(true);
                setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [enabled, resetKey, reloadToken]);

    const hasMore = !state.exhausted && state.items.length < state.total;

    const loadMore = useCallback(() => {
        const edge = state.items.at(-1);
        if (!edge || loading.current || requested.current.has(edge.id)) return;
        requested.current.add(edge.id);
        loading.current = true;
        setIsLoadingMore(true);
        void loadPageRef
            .current(edge.id)
            .then((page) => {
                setState((prev) => {
                    const known = new Set(prev.items.map((it) => it.id));
                    const fresh = page.items.filter((it) => !known.has(it.id));
                    return {
                        items: fresh.length > 0 ? [...prev.items, ...fresh] : prev.items,
                        total: page.total,
                        // Пустая страница = лента кончилась, даже если `total`
                        // разошёлся с показанным (медиа удалили между запросами).
                        exhausted: fresh.length === 0,
                    };
                });
            })
            .catch((err) => {
                console.error("chat media feed: failed to load more", err);
                requested.current.delete(edge.id);
            })
            .finally(() => {
                loading.current = false;
                setIsLoadingMore(false);
            });
    }, [state.items]);

    const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

    return {
        items: state.items,
        total: state.total,
        hasMore,
        isLoading,
        isLoadingMore,
        isError,
        loadMore,
        refetch,
    };
}
